import { FC } from 'react';
import type { Discrepancy } from '../../../api/reconciliationApi';
import { StatusBadge } from '../StatusBadge';
import { cn } from '../../../utils/cn';
import { formatCurrencyAmount } from '../../../utils/currency';

interface DiscrepancyTableProps {
  discrepancies: Discrepancy[];
  onResolve: (id: string) => void;
}

function getDiscrepancyVariant(
  type: string | null
): 'error' | 'warning' | 'info' | 'default' {
  if (!type) return 'default';

  const typeMap: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
    AMOUNT_MISMATCH: 'error',
    MISSING_IN_STRIPE: 'warning',
    MISSING_IN_DB: 'warning',
    STATUS_MISMATCH: 'info',
  };

  return typeMap[type] || 'default';
}

export const DiscrepancyTable: FC<DiscrepancyTableProps> = ({ discrepancies, onResolve }) => {
  if (discrepancies.length === 0) {
    return (
      <div className="flex min-h-[12rem] items-center justify-center rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-8 text-center">
        <p className="text-gray-500 dark:text-slate-400">No discrepancies found</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
          <thead className="bg-gray-50 dark:bg-slate-700/50">
            <tr>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400"
              >
                Payment ID
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400"
              >
                Type
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400"
              >
                DB Amount
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400"
              >
                Stripe Amount
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400"
              >
                Difference
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400"
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-slate-700 bg-white dark:bg-slate-800">
            {discrepancies.map((d) => (
              <tr key={d.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                <td className="whitespace-nowrap px-4 py-3">
                  <span className="font-mono text-sm text-gray-900 dark:text-slate-100">
                    {d.paymentId.slice(0, 8)}...
                  </span>
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  <StatusBadge
                    status={d.discrepancyType || d.status}
                    variant={getDiscrepancyVariant(d.discrepancyType)}
                  />
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 dark:text-slate-100">
                  {formatCurrencyAmount(d.dbAmount)}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500 dark:text-slate-400">
                  {d.stripeAmount !== null ? formatCurrencyAmount(d.stripeAmount) : '-'}
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  {d.discrepancyAmount !== null ? (
                    <span
                      className={cn(
                        'text-sm font-medium',
                        d.discrepancyAmount > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500'
                      )}
                    >
                      {formatCurrencyAmount(Math.abs(d.discrepancyAmount))}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  <button
                    onClick={() => onResolve(d.id)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Resolve
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
